/**
 * FlowNotes API Client - Shortcuts
 * Sidebar shortcuts (pinned documents) management
 */

import { fetchWithAuth, API_BASE_URL } from './core';
import { normalizeExplorerPath } from '@/lib/explorer-tree-focus';

export interface SidebarShortcut {
  id: string;
  /** Sidebar workspace (= organizations row id) this pin belongs to */
  organization_id: string | null;
  document_id: string | null;
  folder_path: string | null;
  label: string | null;
  icon: string | null;
  sort_order: number;
  created_at: string;
  is_personal: boolean;
  document_title?: string;
  document_type?: string;
  document_path?: string;
}

/** Match a context-menu id or path to a stored shortcut. Explorer rows use paths; the API stores a document UUID. */
export function findPinnedSidebarShortcut(
  shortcuts: SidebarShortcut[],
  rawIdOrPath: string,
): SidebarShortcut | undefined {
  const t = String(rawIdOrPath ?? '').trim();
  if (!t) return undefined;
  if (/^[0-9a-f-]{36}$/i.test(t)) {
    return shortcuts.find((s) => s.document_id === t);
  }
  const norm = normalizeExplorerPath(t);
  return shortcuts.find(
    (s) =>
      normalizeExplorerPath(String(s.document_path ?? '')) === norm ||
      normalizeExplorerPath(String(s.folder_path ?? '')) === norm,
  );
}

/**
 * Fetch user's pinned sidebar shortcuts
 */
export async function getSidebarShortcuts(): Promise<{ success: boolean; shortcuts: SidebarShortcut[] }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/sidebar-shortcuts`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch sidebar shortcuts');
  }

  return response.json();
}

/**
 * Pin a document to sidebar
 */
export async function createSidebarShortcut(params: {
  document_id?: string;
  folder_path?: string;
  label?: string;
  icon?: string;
  sort_order?: number;
}): Promise<{ success: boolean; shortcut: SidebarShortcut }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/sidebar-shortcuts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to pin document');
  }

  return response.json();
}

/**
 * Update sidebar shortcut (label, icon, sort_order)
 */
export async function updateSidebarShortcut(
  id: string,
  params: {
    label?: string;
    icon?: string;
    sort_order?: number;
  }
): Promise<{ success: boolean; shortcut: SidebarShortcut }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/sidebar-shortcuts/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update shortcut');
  }

  return response.json();
}

/**
 * Unpin a document from sidebar
 */
export async function deleteSidebarShortcut(id: string): Promise<{ success: boolean }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/sidebar-shortcuts/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unpin document');
  }

  return response.json();
}

/**
 * Batch reorder shortcuts
 */
export async function reorderSidebarShortcuts(
  shortcuts: { id: string; sort_order: number }[]
): Promise<{ success: boolean }> {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/sidebar-shortcuts/reorder`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ shortcuts }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reorder shortcuts');
  }

  return response.json();
}
